import React, { useEffect, useState, useCallback } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { TagService } from "../../services/TagService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  PlayIcon,
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  ClockIcon,
} from "lucide-react";
import { TagDefinition } from "./TagDefinitionModal";
import { toast } from "sonner";

interface TagRun {
  id: number;
  status: string;
  total_evaluated: number;
  total_matched: number;
  date_created: number;
  date_completed?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  tag: TagDefinition | null;
}

const STATUS_CONFIG: Record<
  string,
  { icon: React.ReactNode; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  COMPLETED: {
    icon: <CheckCircle2Icon className="w-3.5 h-3.5" />,
    variant: "default",
  },
  RUNNING: {
    icon: <Loader2Icon className="w-3.5 h-3.5 animate-spin" />,
    variant: "secondary",
  },
  PENDING: {
    icon: <ClockIcon className="w-3.5 h-3.5" />,
    variant: "outline",
  },
  FAILED: {
    icon: <XCircleIcon className="w-3.5 h-3.5" />,
    variant: "destructive",
  },
};

const TagRunDrawer: React.FC<Props> = ({ open, onClose, tag }) => {
  const { apiKey } = usePermissions();
  const [runs, setRuns] = useState<TagRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const loadRuns = useCallback(() => {
    if (!tag?.id) return;
    TagService.listRuns(tag.id, setLoading, apiKey).subscribe(
      (data: TagRun[]) => setRuns(data),
      (err: any) => console.error("Failed to load runs", err)
    );
  }, [tag?.id, apiKey]);

  useEffect(() => {
    if (open && tag?.id) loadRuns();
  }, [open, tag?.id, loadRuns]);

  useEffect(() => {
    if (!open) return;
    const hasActive = runs.some(
      (r) => r.status === "RUNNING" || r.status === "PENDING"
    );
    if (!hasActive) return;

    const interval = setInterval(loadRuns, 3000);
    return () => clearInterval(interval);
  }, [open, runs, loadRuns]);

  const handleTrigger = () => {
    if (!tag?.id) return;
    setTriggering(true);
    TagService.triggerRun(tag.id, {}, setTriggering, apiKey).subscribe(
      (run: TagRun) => {
        setRuns((prev) => [run, ...prev]);
        toast.success("Tag run started");
        setTriggering(false);
      },
      (err: any) => {
        console.error("Failed to trigger run", err);
        toast.error("Failed to start run");
        setTriggering(false);
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Runs: {tag?.name}</DialogTitle>
          <DialogDescription>
            Batch execute this tag rule against your data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Button
            onClick={handleTrigger}
            disabled={triggering}
            className="w-full gap-2"
          >
            {triggering ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <PlayIcon className="w-4 h-4" />
            )}
            Run Now
          </Button>

          <div className="space-y-2">
            {loading && runs.length === 0 ? (
              <div className="flex justify-center py-8">
                <Loader2Icon className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : runs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No runs yet.
              </p>
            ) : (
              runs.map((run) => {
                const cfg = STATUS_CONFIG[run.status] || STATUS_CONFIG.PENDING;
                return (
                  <div
                    key={run.id}
                    className="border rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <Badge variant={cfg.variant} className="gap-1 text-xs">
                        {cfg.icon}
                        {run.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {run.date_created
                          ? new Date(run.date_created).toLocaleString()
                          : ""}
                      </span>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span>
                        Evaluated:{" "}
                        <strong>{run.total_evaluated.toLocaleString()}</strong>
                      </span>
                      <span>
                        Matched:{" "}
                        <strong>{run.total_matched.toLocaleString()}</strong>
                      </span>
                      {run.total_evaluated > 0 && (
                        <span>
                          Rate:{" "}
                          <strong>
                            {(
                              (run.total_matched / run.total_evaluated) *
                              100
                            ).toFixed(1)}
                            %
                          </strong>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TagRunDrawer;
